import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Slide from "@mui/material/Slide";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import { StyledAnchor } from "@/components/common/styles";
import { marketingPalette } from "@/components/marketing/tokens";
import NavActions, { signUpSx } from "./NavActions";
import { ChevronIcon } from "./navIcons";
import NavMenuContent from "./NavMenuContent";
import {
  NAV_MENUS,
  PRICING_LINK,
  SIGN_UP_LINK,
  type NavMenu,
} from "./navigation";
import { TINY, WIDE } from "@/components/marketing/tokens";
import {
  DRAWER_MOTION,
  DRAWER_PANEL_MOTION,
  DRAWER_TOP,
  PANEL_MOTION,
} from "./tokens";

const BAR_LINE = { width: 16, height: 2, backgroundColor: "#B7BFC6" };

/**
 * The published bars morph into a cross in two beats: the outer rules meet in
 * the middle, then turn — and unwind in the opposite order. `translate` and
 * `rotate` are separate properties, so each beat carries its own duration,
 * which a single `transform` could not.
 */
const CROSS = [
  { shift: "0 6px", angle: "-135deg" },
  { shift: "0 -6px", angle: "-45deg" },
];

const MORPH = {
  open: `translate ${DRAWER_MOTION.converge}ms ease-out, rotate ${DRAWER_MOTION.rotate}ms ease-out ${DRAWER_MOTION.converge}ms`,
  closed: `rotate ${DRAWER_MOTION.rotate}ms ease-out, translate ${DRAWER_MOTION.converge}ms ease-out ${DRAWER_MOTION.rotate}ms`,
};

const AT_REST = { shift: "0 0", angle: "0deg" };

/** Every drawer row, whether it discloses a menu or just navigates. */
const DRAWER_ROW = {
  display: "flex",
  // `ButtonBase` leaves the UA's own button font in place.
  fontFamily: "inherit",
  alignItems: "center",
  justifyContent: "space-between",
  columnGap: "6.08px",
  width: 1,
  height: 48,
  p: "16px 12.8px",
  borderRadius: "8px",
  border: `1px solid ${marketingPalette.hairline}`,
  backgroundColor: "background.default",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: "24px",
};

function barMotion(open: boolean, bar: (typeof CROSS)[number]) {
  const state = open ? bar : AT_REST;
  return {
    ...BAR_LINE,
    translate: state.shift,
    rotate: state.angle,
    transition: open ? MORPH.open : MORPH.closed,
  };
}

/** The middle rule only vanishes, and it waits for the beat it is not part of. */
function middleMotion(open: boolean) {
  const wait = open ? DRAWER_MOTION.converge : DRAWER_MOTION.rotate;
  return {
    ...BAR_LINE,
    opacity: open ? 0 : 1,
    transition: `opacity 0ms ${wait}ms`,
  };
}

function MenuButton({
  open,
  controls,
  onClick,
  buttonRef,
}: {
  open: boolean;
  controls?: string;
  onClick: () => void;
  buttonRef: React.Ref<HTMLButtonElement>;
}) {
  return (
    <ButtonBase
      ref={buttonRef}
      aria-label="Menu"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onClick}
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        flexDirection: "column",
        height: "53px",
        p: "20px 25px",
        gap: 1,
      }}
    >
      <Box sx={barMotion(open, CROSS[0])} />
      <Box sx={middleMotion(open)} />
      <Box sx={barMotion(open, CROSS[1])} />
    </ButtonBase>
  );
}

/**
 * The groups behind a row's curtain: hidden and 20px high until they fade up
 * together, and out in the panel's 125ms. One wrapper, as the published groups
 * move as one.
 */
function contentMotion(open: boolean) {
  const { contentDelay, contentDuration, contentEase } = DRAWER_PANEL_MOTION;
  const timing = open
    ? `${contentDuration}ms ${contentEase} ${contentDelay}ms`
    : `${PANEL_MOTION.exit}ms ${PANEL_MOTION.exitEase}`;
  return {
    opacity: open ? 1 : 0,
    transform: open ? "none" : `translateY(-${PANEL_MOTION.rise}px)`,
    transition: `opacity ${timing}, transform ${timing}`,
  };
}

function DrawerMenu({
  menu,
  open,
  onToggle,
  onNavigate,
}: {
  menu: NavMenu;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const panelId = `${useId()}-panel`;
  return (
    <Box>
      <ButtonBase
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        sx={DRAWER_ROW}
      >
        {menu.label}
        <ChevronIcon
          sx={{
            fontSize: 16,
            transform: open ? "rotate(180deg)" : "none",
            transition: `transform ${PANEL_MOTION.duration}ms ease`,
          }}
        />
      </ButtonBase>
      <Collapse
        id={panelId}
        in={open}
        timeout={{
          enter: PANEL_MOTION.duration,
          exit: PANEL_MOTION.drawerExit,
        }}
        easing={{
          enter: DRAWER_PANEL_MOTION.curtainEase,
          exit: PANEL_MOTION.exitEase,
        }}
      >
        <Box sx={contentMotion(open)}>
          <NavMenuContent
            menu={menu}
            variant="drawer"
            onNavigate={onNavigate}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Everything the drawer covers: the bar's siblings, less whatever a third party
 * floats over the whole document, which the drawer is drawn under rather than
 * over.
 */
function pageBehind(bar: Element | null | undefined) {
  return [...(bar?.parentElement?.children ?? [])].filter(
    (node): node is HTMLElement =>
      node !== bar &&
      node instanceof HTMLElement &&
      getComputedStyle(node).position !== "fixed"
  );
}

/**
 * Pins the page behind the drawer, as the published one does while open, and
 * takes it out of the tab order: the drawer paints over the whole viewport and
 * the page underneath cannot scroll, so a tab reaching it strands focus on a
 * control that can be neither seen nor scrolled to. Only the page goes inert.
 * The bar stays live, which is what keeps this a disclosure and not a dialog.
 */
function usePageBehindDrawer(
  open: boolean,
  navRef: React.RefObject<HTMLElement | null>
) {
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const behind = pageBehind(navRef.current?.closest("header"));
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    behind.forEach((node) => node.setAttribute("inert", ""));
    return () => {
      document.body.style.overflow = previous;
      behind.forEach((node) => node.removeAttribute("inert"));
    };
  }, [open, navRef]);
}

/**
 * Below 992 the bar keeps the wordmark, a Sign Up button and a hamburger, and
 * everything else moves into a drawer that slides down over the page.
 *
 * The published drawer is not a dialog — it paints no backdrop, leaves the bar
 * live above it and never holds focus — so this stays a disclosure. What it
 * does borrow from a dialog is Escape, and what it does to the page it covers.
 *
 * Both bar controls are positioned against the bar rather than the content
 * column, because the published ones sit 72px and 0 from the viewport edge
 * while the column keeps a 16px gutter.
 */
export default function MobileNav({
  actions = true,
}: {
  /** The bar's Sign Up and the drawer's buttons, which one published bar leaves out. */
  actions?: boolean;
}) {
  const drawerId = `${useId()}-drawer`;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const wide = useMediaQuery(WIDE);
  usePageBehindDrawer(open, rootRef);

  // The bar hides this nav above the wide step and the published one closes
  // with it, so a drawer left open would hold the page inert with nothing on
  // screen to close it.
  useEffect(() => {
    if (wide) {
      setOpen(false);
      setExpanded(null);
    }
  }, [wide]);

  const close = () => {
    setOpen(false);
    setExpanded(null);
  };

  const toggle = () => {
    if (open) {
      close();
    } else {
      setOpen(true);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      buttonRef.current?.focus();
      close();
    }
  };

  return (
    <Box ref={rootRef} onKeyDown={onKeyDown}>
      {actions ? (
        <StyledAnchor
          href={SIGN_UP_LINK.href}
          sx={{
            ...signUpSx("bar"),
            position: "absolute",
            top: "8.8px",
            right: "72px",
            [TINY]: { right: "64px" },
            height: "35.19px",
            p: 3,
          }}
        >
          {SIGN_UP_LINK.label}
        </StyledAnchor>
      ) : null}
      <MenuButton
        buttonRef={buttonRef}
        open={open}
        controls={open ? drawerId : undefined}
        onClick={toggle}
      />
      {/* Clips the slide at the bar's rule, so the drawer emerges from under
          the bar rather than passing over it. */}
      <Box
        sx={{
          position: "absolute",
          top: `${DRAWER_TOP}px`,
          left: 0,
          right: 0,
          // `dvh`: Safari's `vh` is the large viewport and hides the last control.
          height: `calc(100dvh - ${DRAWER_TOP}px)`,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <Slide
          direction="down"
          in={open}
          timeout={DRAWER_MOTION.slide}
          easing="ease"
          mountOnEnter
          unmountOnExit
        >
          <Box
            id={drawerId}
            component="nav"
            aria-label="Main"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "100%",
              overflowY: "auto",
              pointerEvents: "auto",
              backgroundColor: "background.default",
              p: "0 32px 48px",
              [TINY]: { p: "0 16px 48px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                rowGap: "12.8px",
                mt: "16px",
              }}
            >
              {NAV_MENUS.map((menu) => (
                <DrawerMenu
                  key={menu.id}
                  menu={menu}
                  open={expanded === menu.id}
                  onToggle={() =>
                    setExpanded((current) =>
                      current === menu.id ? null : menu.id
                    )
                  }
                  onNavigate={close}
                />
              ))}
              <StyledAnchor
                href={PRICING_LINK.href}
                onClick={close}
                sx={{ ...DRAWER_ROW, maxWidth: 1 }}
              >
                {PRICING_LINK.label}
              </StyledAnchor>
            </Box>
            {actions ? (
              <NavActions variant="drawer" onNavigate={close} />
            ) : null}
          </Box>
        </Slide>
      </Box>
    </Box>
  );
}
